// Pure helpers for the air/truck map layers (no JSX/leaflet, runs under node --test).
import { passesCountryFilter } from './countryFilters.js';

// The air/truck views carry no origin_country; the documented origin is Peru.
const MODE_ORIGIN_COUNTRY = 'Peru';

/**
 * Selection / hover / React key. container_number is the persistent group id
 * once the lake views ship; until then (absent or equal to the AWB / truck id
 * alone) fall back to the full group tuple so distinct cards never collide.
 */
const REGISTRY_ID = /^(AIR|TRUCK)-[0-9a-f]{16}$/;

/** 'air' | 'truck' | 'sea'. Sea rows carry no discriminator, so absence is sea. */
export function rowTransportMode(row) {
  const m = String(row?.transport_mode ?? '').toLowerCase();
  return m === 'air' || m === 'truck' ? m : 'sea';
}

/**
 * Sea rows go through the ShipsGo pipeline — vessel markers, voyage routes,
 * fly-to. Air and truck rows share the details panel but none of that, so every
 * sea-only consumer gates on this.
 */
export const isSeaRow = (row) => rowTransportMode(row) === 'sea';

export function modeRowKey(row) {
  const mode = row?.transport_mode;
  const cn = row?.container_number == null ? '' : String(row.container_number);
  // Registry group ids are persistent across ETA corrections. Legacy rows
  // (container_number equal to the bare AWB / truck id) use the full tuple.
  const legacyId = row?.awb || row?.truck_id;
  const persistent = REGISTRY_ID.test(cn)
    || (cn !== '' && row?.truck_id == null && String(row?.awb ?? '') !== cn);
  if (persistent) return JSON.stringify([mode, cn]);
  return JSON.stringify([
    mode,
    legacyId || cn || null,
    row?.origin_region || row?.departure_port || null,
    row?.arrival_port || null,
    row?.destination_country || null,
    row?.etd || null,
    row?.eta || null,
  ]);
}

export const hasCommodity = (row, commodity) => String(row?.commodities || '')
  .split(',')
  .some((c) => c.trim().toLowerCase() === String(commodity).trim().toLowerCase());

/**
 * Commodity AND origin/destination predicate, applied independently of sea.
 * Returns a new array; rows get origin_country=Peru when the view omits it.
 */
export function filterModeRows(rows, mode, commodity, originSel, destSel) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r) => ({ ...r, transport_mode: mode, origin_country: r.origin_country || MODE_ORIGIN_COUNTRY }))
    .filter((r) => (!commodity || hasCommodity(r, commodity)) && passesCountryFilter(r, originSel, destSel));
}
